import { useCallback, useEffect, useRef, useState } from 'react';
import {
  dontShowAnnouncementAgain,
  getActiveAnnouncements,
  markAnnouncementAsSeen,
} from '../../../api/announcements.api';
import type { ActiveAnnouncement } from '../../../types/api/announcements.types';
import { isKnownAnnouncementType } from '../helpers/announcementContent';

/** How long to let the page settle before interrupting it. */
const SETTLE_DELAY_MS = 700;

/**
 * The announcements this user should be shown right now, as one carousel.
 *
 * `/active` is the single source of truth - the backend already applied
 * activity, the publish window, min version, targeting and this user's seen
 * state, so nothing is filtered here except types this build cannot draw.
 *
 * All of them open together and the user pages through them, instead of a
 * modal per announcement that closes and reopens. Each one counts as seen
 * the moment its slide is shown: closing on the second of four leaves the
 * last two unseen, and they come back on the next fetch.
 *
 * Refetched when the tab comes back to the foreground and nothing is open,
 * because a repeating announcement is due back after its interval and a
 * merchant tablet stays open for days. Ids marked this session are kept out
 * locally: the POST races the refetch, and a just-seen one must not pop back.
 *
 * That local set is deliberately a ref and nothing more. Seen state is per
 * (user, announcement, app, platform) on the backend, so an announcement
 * dismissed on the phone is still due on the dashboard - persisting "seen
 * #12" anywhere shared, or in storage this bundle reads on the next load,
 * would hide it on a platform the backend still wants to show it on.
 */
export const useActiveAnnouncements = () => {
  const [items, setItems] = useState<ActiveAnnouncement[]>([]);
  const [index, setIndex] = useState(0);
  const seenIds = useRef(new Set<number>());

  const fetchActive = useCallback(async () => {
    try {
      const { data } = await getActiveAnnouncements();
      const fresh = (data?.data ?? []).filter(
        (a) => isKnownAnnouncementType(a.type) && !seenIds.current.has(a.id)
      );
      setIndex(0);
      setItems(fresh);
    } catch {
      // Silent: a merchant who cannot be told what's new right now should not
      // be told that telling them failed. The next foreground retries.
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchActive, SETTLE_DELAY_MS);

    // Only when nothing is on screen - a refetch mid-read would reshuffle the
    // slides under the reader if the backend reordered the list.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      setItems((prev) => {
        if (!prev.length) fetchActive();
        return prev;
      });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchActive]);

  // Seen when shown. Fire-and-forget: a failed POST only means it shows once
  // more next time.
  const current = items[index] ?? null;
  useEffect(() => {
    if (!current || seenIds.current.has(current.id)) return;
    seenIds.current.add(current.id);
    markAnnouncementAsSeen(current.id).catch(() => undefined);
  }, [current]);

  const goTo = useCallback(
    (target: number) =>
      setIndex(Math.max(0, Math.min(target, items.length - 1))),
    [items.length]
  );

  const close = useCallback(() => {
    setItems([]);
    setIndex(0);
  }, []);

  // Silences one slide for good and takes it out of the carousel; the next
  // one slides into its place, and the last one going closes it all.
  const dontShowAgain = useCallback(
    (id: number) => {
      seenIds.current.add(id);
      dontShowAnnouncementAgain(id).catch(() => undefined);
      const rest = items.filter((a) => a.id !== id);
      setItems(rest);
      setIndex((i) => Math.max(0, Math.min(i, rest.length - 1)));
    },
    [items]
  );

  return { items, index, current, goTo, close, dontShowAgain };
};
